import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../../api/firebase';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import racketImg from '../../assets/images/racket.webp';
import logoImg from '../../assets/images/logo.svg';
import { isOlderThan16 } from '../../utils/index';

const schema = z.object({
  name: z.string().min(3, { message: 'Adicione um nome válido.' }),
  phoneNumber: z.string().min(11, { message: 'Confira o número de celular.' }),
  email: z.string().email({ message: 'Email inválido, por favor conferir!' }),
  password: z.string().min(6, { message: 'A senha precisa ter pelo menos 6 caracteres.' }),
  birthday: z.string().refine((val) => isOlderThan16(val), {
    message: 'Você deve ter 16 anos ou mais para cadastrar.',
  }),
});

function SignUp() {
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const signupWithEmail = async (data: any) => {
    try {
      const { email, password, name } = data;
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(user, { displayName: name });
      navigate('/login');
    } catch (e) {
      console.error(e);
    }
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema) });

  return (
    <div className="w-full min-h-screen md:grid md:grid-cols-2" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Form side */}
      <div className="flex h-screen items-center justify-center p-6 md:h-auto md:p-0 md:py-8">
        <div className="mx-auto grid w-[380px] gap-2">
          <form onSubmit={handleSubmit(signupWithEmail)} className="flex flex-col gap-5">
            <div className="flex flex-col items-center gap-2 text-center">
              <img src={logoImg} alt="Apertai" style={{ width: '100px' }} className="mb-2" />
              <h1 className="text-2xl font-bold text-white">Criar sua conta</h1>
              <p className="text-sm text-zinc-500">Preencha os dados abaixo para se cadastrar</p>
            </div>
            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="name">Nome completo</Label>
                <Input id="name" type="text" placeholder="Seu nome" {...register('name')} />
                {errors.name?.message && <p className="text-red-400 text-xs">{String(errors.name.message)}</p>}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="seu@email.com" {...register('email')} />
                {errors.email?.message && <p className="text-red-400 text-xs">{String(errors.email.message)}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="phoneNumber">Celular</Label>
                  <Input id="phoneNumber" type="tel" placeholder="(31) 99999-9999" {...register('phoneNumber')} />
                  {errors.phoneNumber?.message && <p className="text-red-400 text-xs">{String(errors.phoneNumber.message)}</p>}
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="birthday">Data de nascimento</Label>
                  <Input id="birthday" type="date" {...register('birthday')} />
                  {errors.birthday?.message && <p className="text-red-400 text-xs">{String(errors.birthday.message)}</p>}
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="password">Crie uma senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="******"
                    {...register('password')}
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex h-full w-10 items-center justify-center text-zinc-500 hover:text-white bg-transparent border-none cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password?.message && <p className="text-red-400 text-xs">{String(errors.password.message)}</p>}
              </div>

              {/* Terms checkbox */}
              <label className="flex items-start gap-2 cursor-pointer mt-1">
                <input
                  type="checkbox"
                  checked={isTermsAccepted}
                  onChange={(e) => setIsTermsAccepted(e.target.checked)}
                  className="mt-1 accent-orange-500"
                />
                <span className="text-zinc-500 text-xs leading-relaxed">
                  Li e aceito os{' '}
                  <a href="/termos-de-uso.pdf" target="_blank" className="text-orange-400 hover:underline no-underline">
                    termos de uso
                  </a>{' '}
                  e a política de privacidade.
                </span>
              </label>

              <button
                type="submit"
                disabled={!isTermsAccepted}
                className="w-full h-10 rounded-lg font-semibold text-white border-none cursor-pointer text-sm transition-all hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed mt-1"
                style={{ background: 'linear-gradient(135deg, #e57605, #f29b2a)' }}
              >
                Cadastrar
              </button>
            </div>
          </form>

          <div className="text-center text-sm text-zinc-500 mt-2">
            Já tem uma conta?{' '}
            <Link to="/login" className="text-orange-400 hover:underline no-underline">
              Entrar
            </Link>
          </div>
        </div>
      </div>

      {/* Image side */}
      <div
        className="hidden md:block relative bg-cover bg-center"
        style={{ backgroundImage: `url(${racketImg})` }}
      >
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, #0a0a0a, transparent 40%)' }}
        />
        <div className="relative z-10 flex h-full flex-col items-center justify-end p-6 pb-12">
          <p className="text-lg font-medium text-white text-center">
            "Grave cada lance com qualidade. A Apertai cuida do resto."
          </p>
          <span className="text-sm text-zinc-400 mt-2">— Apertai</span>
        </div>
      </div>
    </div>
  );
}

export default SignUp;
